import { useEffect, useRef, useState } from 'react';
import { Heart } from 'lucide-react';
import { cn } from '@worldcoo/ui/lib/utils';
import { usePrefersReducedMotion } from '@worldcoo/ui/hooks/use-prefers-reduced-motion';
import { LottiePlayer } from './lottie-player';
import footerAnimation from './footer.json';

/** Both the press and the orange: the colour IS the press, not a hold timed separately from it. */
const PRESS_MS = 130;

/**
 * The only thing that moves: a rigid translate of the hand, leaning right because the arm runs ~6
 * degrees off vertical. docs/notes/portals/footer-heart.md
 */
const ARM_REST = '2px -8px';
const ARM_HOVER = '3px -20px';
/** A poke: six units past `ARM_HOVER`, out in 120ms and back in 190ms, as equal durations read mechanical. */
const ARM_PRESS = '4px -26px';

const SCENE = 'new-footer-heart';

/** Not `--color-alert`: that is the form-error colour, and restyling errors must not drag the heart along. */
const HEART_PRESSED = 'var(--color-brand)';

/** `brand-strong` keeps a particle legible while it crosses the pressed heart. */
const BURST_COLOURS = ['var(--color-brand)', 'var(--color-brand-strong)'] as const;

/** ~(219, 119) of the 328×384 board, from the heart layer in `footer.json`; the box resizes at `xl`. */
const HEART_CENTRE = { x: '66.8%', y: '30.9%' };

/** `MAX_LIVE` drops the oldest, for when a backgrounded tab delays the `animationend` that removes one. */
const PER_TAP = 8;
const MAX_LIVE = 40;

type Particle = {
	id: number;
	/** Custom properties the keyframes read. */
	style: React.CSSProperties;
	colour: string;
	size: number;
};

/** `translate`, not `transform`, which lottie writes itself: docs/notes/portals/footer-heart.md */
const CSS = `
.${SCENE} { pointer-events: none; }
.${SCENE} .wc-heart { pointer-events: auto; cursor: pointer; -webkit-user-select: none; user-select: none; }
.${SCENE} .wc-hand {
	translate: ${ARM_REST};
	transition: translate 190ms cubic-bezier(0.22, 0.61, 0.36, 1);
}
.${SCENE}:hover .wc-hand { translate: ${ARM_HOVER}; }
.${SCENE}[data-pressed] .wc-hand { translate: ${ARM_PRESS}; transition-duration: 120ms; }
.${SCENE} .wc-heart path { transition: fill 70ms ease-out; }
.${SCENE}[data-pressed] .wc-heart path { fill: ${HEART_PRESSED}; }
.${SCENE}-pop {
	position: absolute;
	animation: ${SCENE}-pop var(--dur) cubic-bezier(0.16, 0.72, 0.32, 1) forwards;
}
@keyframes ${SCENE}-pop {
	0% { translate: 0 0; scale: 0.3; opacity: 0; }
	18% { opacity: 1; }
	100% { translate: var(--dx) var(--dy); scale: var(--s); opacity: 0; }
}
@media (prefers-reduced-motion: reduce) {
	.${SCENE}:hover .wc-hand,
	.${SCENE}[data-pressed] .wc-hand { translate: ${ARM_REST}; transition: none; }
}`;

function burst(seed: number): Particle[] {
	return Array.from({ length: PER_TAP }, (_, i) => {
		// A jittered even sweep, as eight random angles often leave a bald quadrant; biased upward.
		const angle = -Math.PI / 2 + ((i - (PER_TAP - 1) / 2) / PER_TAP) * Math.PI * 1.6 + (Math.random() - 0.5) * 0.4;
		const distance = 42 + Math.random() * 46;
		const size = 11 + Math.round(Math.random() * 9);
		return {
			id: seed + i,
			colour: BURST_COLOURS[i % BURST_COLOURS.length]!,
			size,
			style: {
				'marginLeft': -size / 2,
				'marginTop': -size / 2,
				'rotate': `${Math.round((Math.random() - 0.5) * 70)}deg`,
				'--dx': `${Math.round(Math.cos(angle) * distance)}px`,
				'--dy': `${Math.round(Math.sin(angle) * distance)}px`,
				'--s': (0.7 + Math.random() * 0.5).toFixed(2),
				'--dur': `${700 + Math.round(Math.random() * 400)}ms`,
			} as React.CSSProperties,
		};
	});
}

/** Decorative and deliberately not focusable: docs/notes/portals/footer-heart.md */
export function FooterHeart(props: { className?: string; ariaLabel?: string }) {
	const reduce = usePrefersReducedMotion();
	const [pressed, setPressed] = useState(false);
	const [particles, setParticles] = useState<Particle[]>([]);
	const press = useRef<ReturnType<typeof setTimeout>>(undefined);
	const nextId = useRef(0);

	// The footer is on every page, so a tap then a navigation would fire into an unmounted component.
	useEffect(() => () => clearTimeout(press.current), []);

	const tap = (event: React.PointerEvent<HTMLDivElement>) => {
		// For the day something else in this box becomes interactive; today only the heart is hit-testable.
		if (!(event.target as Element).closest?.('.wc-heart')) return;

		// Spamming the heart otherwise selects the footer's copy as a drag or a double-click.
		event.preventDefault();

		// Restarts the beat, so a second tap never has its press cut short by the first one's timeout.
		clearTimeout(press.current);
		setPressed(true);
		press.current = setTimeout(() => setPressed(false), PRESS_MS);

		if (reduce) return;
		// Randomness stays out of render, which must match the server's empty list.
		const seed = (nextId.current += PER_TAP);
		setParticles((live) => [...live, ...burst(seed)].slice(-MAX_LIVE));
	};

	return (
		// The ratio is load-bearing: a bare `w-*` box is 0px tall and `playWhenVisible` never sees it intersect.
		<div
			className={cn(SCENE, 'relative', props.className)}
			style={{ aspectRatio: `${footerAnimation.w} / ${footerAnimation.h}` }}
			data-pressed={pressed || undefined}
			onPointerDown={tap}
			// Only Chrome drops the compatibility `mousedown` for a cancelled `pointerdown`, and it starts selections.
			onMouseDown={(event) => {
				if ((event.target as Element).closest?.('.wc-heart')) event.preventDefault();
			}}
		>
			<style href={SCENE} precedence="medium" dangerouslySetInnerHTML={{ __html: CSS }} />
			<LottiePlayer
				animationData={footerAnimation}
				loop={false}
				playWhenVisible
				ariaLabel={props.ariaLabel}
				className="h-full w-full"
			/>
			{/* Inherited from the wrapper already; stated because eating a link's click is too costly. */}
			<span aria-hidden className="pointer-events-none absolute" style={{ left: HEART_CENTRE.x, top: HEART_CENTRE.y }}>
				{particles.map((p) => (
					<Heart
						key={p.id}
						className={`${SCENE}-pop`}
						style={{ ...p.style, width: p.size, height: p.size, color: p.colour }}
						fill="currentColor"
						stroke="none"
						onAnimationEnd={() => setParticles((live) => live.filter((q) => q.id !== p.id))}
					/>
				))}
			</span>
		</div>
	);
}
